export type ActiveDialog =
  | 'upgrade_hard'
  | 'close_sale'
  | 'cancel'
  | 'edit_dates'
  | 'override_status'
  | 'force_release'
  | 'expiry_deadline'
  | null;

export interface ReservationDialogLabels {
  // Confirm dialog (upgrade_hard, close_sale)
  upgradeHardTitle: string;
  upgradeHardBody: string;
  closeSaleTitle: string;
  closeSaleBody: string;
  forceReleaseTitle: string;
  forceReleaseBody: string;
  confirmLabel: string;
  cancelBtnLabel: string;
  // Cancel dialog
  cancelTitle: string;
  cancelBody: (mediaUnitName: string, dateFrom: string, dateTo: string) => string;
  cancelConfirmLabel: string;
  cancelWillBeFreeLabel: string;
  /** Whole-versus-selected-period release choice (CU-86cbh03pj parity, task 4.1) —
   * reuses the same `ReleaseModeChoice` component `UnifiedSelectPopover` uses. */
  releaseModeChoiceTitle: string;
  releaseModeChoiceWholeLabel: string;
  releaseModeChoiceSelectedLabel: string;
  // Edit dates dialog
  editDatesTitle: string;
  /**
   * Label shown above the single from->to range trigger (DateRangeButtonField).
   * Optional so existing/mockup callers built against the pre-migration
   * interface keep typechecking; omitting it just renders the trigger with
   * no label above it.
   */
  dateRangeLabel?: string;
  displacementWarningLabel: string;
  forceCheckboxLabel: string;
  saveLabel: string;
  // Override status dialog
  overrideStatusTitle: string;
  targetStatusLabel: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  reasonRequiredLabel: string;
  auditNoteLabel: string;
  overrideConfirmLabel: string;
  /** Label for the optional manual TTL expiry override field (SOFT/HARD targets only). */
  ttlExpiresAtLabel: string;
  /** Helper text explaining the TTL override is optional. */
  ttlExpiresAtHint: string;
  /**
   * Placeholder shown in the empty TTL override date picker trigger.
   * Optional for the same reason as `dateRangeLabel` above — falls back to
   * `DatePickerButton`'s own default placeholder when omitted.
   */
  ttlExpiresAtPlaceholder?: string;
  // Expiry-deadline edit dialog (CU-86cb7qgc9). All optional, same rationale
  // as dateRangeLabel/ttlExpiresAtPlaceholder above — existing/mockup
  // callers built before this dialog type existed keep typechecking;
  // omitting them just renders that string as empty since the dialog only
  // mounts when the caller explicitly sets activeDialog='expiry_deadline'.
  extendExpiryTitle?: string;
  extendExpiryDescription?: string;
  extendExpiryCurrentLabel?: string;
  extendExpiryNewLabel?: string;
  extendExpiryDatePlaceholder?: string;
  extendExpiryReasonLabel?: string;
  extendExpiryReasonPlaceholder?: string;
  extendExpiryReasonRequiredLabel?: string;
  extendExpirySaveLabel?: string;
}

/**
 * `ModalDialog` wires whatever `onClose` it's given to its header X button, backdrop
 * click, and Escape keydown listener — none of which respect a `disabled` attribute.
 * Every dialog in this module passes its `onClose` through this guard (instead of a
 * raw ternary duplicated per component) so the mutation-in-flight guard can't drift
 * between the four call sites.
 */
export function guardClose(onClose: () => void, isPending: boolean): () => void {
  return isPending ? () => {} : onClose;
}
